/**
 * The service worker answered with an error. `messageId` is the translation
 * chosen by the worker (`chooseErrorMessage`), so the page can show a precise
 * toast without parsing the message.
 */
export class ChannelRequestError extends Error {
  readonly messageId: string | null;
  readonly cause: unknown;
  /** e.g. `documentationChannel.getChildren`; `null` when built outside a channel request. */
  readonly operation: string | null;
  /** Correlates this error with the worker's log of the same request. */
  readonly requestId: string | null;

  constructor(
    message: string,
    options: {
      messageId: string | null;
      cause: unknown;
      operation?: string | null;
      requestId?: string | null;
    }
  ) {
    super(message);
    this.name = 'ChannelRequestError';
    this.messageId = options.messageId;
    this.cause = options.cause;
    this.operation = options.operation ?? null;
    this.requestId = options.requestId ?? null;
  }
}

export function isChannelRequestError(error: unknown): error is ChannelRequestError {
  return error instanceof ChannelRequestError;
}

export interface RequestTimeoutDiagnostics extends Record<string, unknown> {
  /** Whether the worker confirmed receiving the request; `null` when not tracked. */
  acknowledged: boolean | null;
  retried: boolean;
}

const CHANNEL_REQUEST_TIMEOUT_PREFIX = 'Channel request timeout';

/** No response in time; carries what is known about the worker for Sentry. */
export class ChannelRequestTimeoutError extends Error {
  readonly operation: string;
  readonly timeoutMs: number;
  readonly diagnostics: RequestTimeoutDiagnostics;
  /** Correlates this timeout with its diagnostics snapshot and the worker's logs. */
  readonly requestId: string | null;

  constructor(
    operation: string,
    timeoutMs: number,
    diagnostics: RequestTimeoutDiagnostics,
    requestId: string | null = null
  ) {
    super(`${CHANNEL_REQUEST_TIMEOUT_PREFIX}: ${operation}`);
    this.name = 'ChannelRequestTimeoutError';
    this.operation = operation;
    this.timeoutMs = timeoutMs;
    this.diagnostics = diagnostics;
    this.requestId = requestId;
  }
}

export function isChannelRequestTimeout(error: unknown): error is ChannelRequestTimeoutError {
  return error instanceof ChannelRequestTimeoutError;
}

/**
 * Nobody acknowledged the request within the policy's acknowledgement window:
 * no context serves the contract (e.g. no page is open to answer the worker).
 * Not reported as a timeout; the caller decides.
 */
export class ChannelRequestNotAcknowledgedError extends Error {
  readonly operation: string;
  readonly acknowledgementTimeoutMs: number;
  readonly requestId: string;

  constructor(operation: string, acknowledgementTimeoutMs: number, requestId: string) {
    super(`Channel request not acknowledged: ${operation}`);
    this.name = 'ChannelRequestNotAcknowledgedError';
    this.operation = operation;
    this.acknowledgementTimeoutMs = acknowledgementTimeoutMs;
    this.requestId = requestId;
  }
}

export function isChannelRequestNotAcknowledged(error: unknown): error is ChannelRequestNotAcknowledgedError {
  return error instanceof ChannelRequestNotAcknowledgedError;
}

/** Events logged for tuning and diagnosis (Sumo only); never failures by themselves. */
export enum ChannelDiagnosticEvent {
  /** A read answered only after its retry: the worker was likely stopped. */
  recoveredAfterRetry = 'channel-request-recovered-after-retry',
  /** A response arrived after the request had timed out. */
  lateResponse = 'channel-request-late-response',
  /** A response took over half of the policy's timeout. */
  slowResponse = 'channel-request-slow',
  /** A fire-and-forget command was not acknowledged by the worker. */
  commandNotAcknowledged = 'channel-command-not-acknowledged',
  /** A request handler failed; the requester got the error and decides how to log it. */
  requestHandlerFailed = 'channel-request-handler-failed',
}
